import React, { RefObject, useRef, useState } from "react";
import { motion, MotionValue, useSpring, useTransform } from "framer-motion";
import { Volume2Icon } from "lucide-react";
import type { Sticker, StickerTone } from "../../data/greetings";
import { spring } from "../../utils/motion";
import { speak } from "../../utils/speech";
interface GreetingStickerProps {
  sticker: Sticker;
  index: number;
  mx: MotionValue<number>;
  my: MotionValue<number>;
  constraintsRef: RefObject<HTMLDivElement>;
  reduce: boolean;
}
const toneClasses: Record<StickerTone, string> = {
  brand: "bg-brand text-white",
  tint: "bg-brand-tint text-ink",
  white: "bg-surface text-ink"
};
const parallaxSpring = {
  stiffness: 120,
  damping: 20,
  mass: 0.6
};
export function GreetingSticker({
  sticker,
  index,
  mx,
  my,
  constraintsRef,
  reduce
}: GreetingStickerProps) {
  const [speaking, setSpeaking] = useState(false);
  const didDrag = useRef(false);
  const px = useSpring(useTransform(mx, v => v * sticker.depth * -26), parallaxSpring);
  const py = useSpring(useTransform(my, v => v * sticker.depth * -20), parallaxSpring);
  const onClick = () => {
    if (didDrag.current) return;
    speak(sticker.speech, sticker.lang, {
      onStart: () => setSpeaking(true),
      onEnd: () => setSpeaking(false)
    });
  };
  return <motion.div className={`absolute ${sticker.mobile ? "" : "hidden lg:block"}`} style={{
    left: `${sticker.x}%`,
    top: `${sticker.y}%`,
    x: reduce ? 0 : px,
    y: reduce ? 0 : py
  }} data-id="element-821" data-mp="bxt024x2uuaa">
      <motion.button type="button" aria-label={`Hear “${sticker.word}” in ${sticker.language}`} onPointerDown={() => didDrag.current = false} onDragStart={() => didDrag.current = true} onClick={onClick} drag dragConstraints={constraintsRef} dragElastic={0.15} dragTransition={{
      bounceStiffness: 400,
      bounceDamping: 18
    }} initial={reduce ? false : {
      opacity: 0,
      y: 24,
      scale: 0.9,
      rotate: sticker.rotate - 12
    }} animate={{
      opacity: 1,
      y: 0,
      scale: 1,
      rotate: sticker.rotate,
      transition: reduce ? {
        duration: 0
      } : {
        ...spring.bouncy,
        delay: 0.25 + index * 0.05
      }
    }} whileHover={reduce ? undefined : {
      scale: 1.05
    }} whileTap={{
      scale: 0.96
    }} whileDrag={{
      scale: 1.1,
      rotate: 0
    }} transition={spring.snappy} className={`relative flex cursor-grab touch-none select-none items-start gap-1.5 rounded-2xl border-3 border-line px-4 py-2.5 text-lg font-bold shadow-brutal outline-none transition-colors duration-150 focus-visible:outline focus-visible:outline-[3px] focus-visible:outline-offset-4 focus-visible:outline-brand active:cursor-grabbing md:px-5 md:py-3 md:text-2xl ${speaking ? "bg-accent text-night" : toneClasses[sticker.tone]}`} data-id="element-822" data-mp="14mgq0dsru7u6">
        <span className="whitespace-nowrap leading-none" data-id="element-823" data-mp="394y1zkaf1xud">{sticker.word}</span>
        {speaking ? <Volume2Icon className="mt-[-3px] h-3.5 w-3.5" strokeWidth={3} aria-hidden="true" data-id="element-824" data-mp="3d4yrbzz1ztg7" /> : <span className={`mt-[-2px] text-[10px] font-semibold leading-none tracking-wide ${sticker.tone === "brand" ? "text-white/80" : "text-ink-2"}`} data-id="element-825" data-mp="11gbssyoukpn5">
            {sticker.code}
          </span>}
      </motion.button>
    </motion.div>;
}